import _ from "lodash";
import React from "react";
import { Tooltip } from "@toddle-edu/ds-web";
import {
  TickCircleOutlined,
  WarningCircleOutlined,
  LockOutlined,
  WarningTriangleOutlined,
  EvaluationNotApplicableOutlined,
  ReSubmissionRequestOutlined,
  LockOpenOutlined,
} from "@toddle-edu/ds-icons";
import { getUserId } from "modules/CommonSelectors";
import {
  formatDate,
  getDateTimeDifference,
  getDateMomentObject,
  isValidDateTimeString,
  checkIsDateBefore,
} from "Utils/DateTimeHelpers";
import { DATE_TIME_CONSTANTS } from "Constants/dateTimeConstants";
import ACLStore from "lib/aclStore";

const getSubmissionStatusIcon = ({ statusV2 }) => {
  switch (statusV2) {
    case "TURNED_IN_ON_TIME":
    case "TURNED_IN_LATE":
      return <TickCircleOutlined variant={"success"} size={"xx-small"} />;
    default:
      return <WarningCircleOutlined variant={"critical"} size={"xx-small"} />;
  }
};

export const getSubmissionStatusIconWithTooltip = ({
  t,
  statusV2,
  isDueDatePassed,
}) => {
  const statusIcon = getSubmissionStatusIcon({ statusV2 });
  let tooltipText = "";

  switch (statusV2) {
    case "TURNED_IN_ON_TIME":
    case "TURNED_IN_LATE":
      tooltipText = t("calendar:assignment_submitted");
      break;

    default:
      tooltipText = isDueDatePassed ? t("calendar:submission_pending") : null;
  }

  return tooltipText ? (
    <Tooltip dsVersion="2.0" tooltip={tooltipText} placement={"right"}>
      {statusIcon}
    </Tooltip>
  ) : null;
};

export const getSubmissionStatus = ({
  t,
  submissionStatus,
  endTime,
  submittedAt,
}) => {
  const submissionStatusToCheck = submissionStatus;

  const commonIconProps = {
    weight: "regular",
    size: "xx-small",
    variant: "default",
  };

  let studentSubmissionStatusLabel = "",
    parentSubmissionStatusLabel = "";

  switch (submissionStatusToCheck) {
    case "SCHEDULED": {
      studentSubmissionStatusLabel = t(
        "calendar:submissions_are_not_yet_open_for_this_assignment"
      );
      return {
        icon: <LockOutlined {...commonIconProps} />,
        studentSubmissionStatusLabel,
        parentSubmissionStatusLabel: studentSubmissionStatusLabel,
        backgroundColor: "var(--decorative-background-subtle-neutral)",
      };
    }

    case "REQUEST_RESUBMISSION_OVERDUE":
    case "OVERDUE": {
      const dayCount = getDateTimeDifference({
        date1: getDateMomentObject(),
        date2: endTime,
        differenceBy: DATE_TIME_CONSTANTS.DAY,
      });

      studentSubmissionStatusLabel = dayCount
        ? t("calendar:your_submission_is_overdue_with_label", {
            count: dayCount,
            dayLabel: dayCount > 1 ? t("common:days") : t("common:day"),
          })
        : t("calendar:your_submission_is_overdue");
      parentSubmissionStatusLabel = dayCount
        ? t("calendar:submission_is_overdue_with_label", {
            count: dayCount,
            dayLabel: dayCount > 1 ? t("common:days") : t("common:day"),
          })
        : t("calendar:submission_is_overdue");
      return {
        icon: (
          <WarningTriangleOutlined {...commonIconProps} variant={"warning"} />
        ),
        studentSubmissionStatusLabel,
        parentSubmissionStatusLabel,
        backgroundColor: "var(--decorative-background-subtle-yellow)",
      };
    }

    case "SUBMISSION_PENDING": {
      const dayCount = getDateTimeDifference({
        date1: endTime,
        date2: getDateMomentObject(),
        differenceBy: DATE_TIME_CONSTANTS.DAY,
      });

      if (dayCount === 0) {
        return {
          icon: (
            <WarningTriangleOutlined {...commonIconProps} variant={"warning"} />
          ),
          studentSubmissionStatusLabel: t(
            "calendar:your_submission_is_due_today",
            {
              time: formatDate({
                date: endTime,
                format: DATE_TIME_CONSTANTS.hh_mm_A,
              }),
            }
          ),
          parentSubmissionStatusLabel: t("calendar:submission_is_due_today", {
            time: formatDate({
              date: endTime,
              format: DATE_TIME_CONSTANTS.hh_mm_A,
            }),
          }),
          backgroundColor: "var(--decorative-background-subtle-yellow)",
        };
      } else if (dayCount === 1) {
        return {
          icon: (
            <WarningTriangleOutlined {...commonIconProps} variant={"warning"} />
          ),
          studentSubmissionStatusLabel: t(
            "calendar:your_submission_is_due_tomorrow"
          ),
          parentSubmissionStatusLabel: t("calendar:submission_is_due_tomorrow"),
          backgroundColor: "var(--decorative-background-subtle-yellow)",
        };
      } else {
        return {
          showSubmissionStatus: false,
        };
      }
    }
    case "SUBMISSION_NOT_REQUIRED": {
      return {
        icon: <EvaluationNotApplicableOutlined {...commonIconProps} />,
        studentSubmissionStatusLabel: t(
          "calendar:this_assignment_does_not_require_submission"
        ),
        parentSubmissionStatusLabel: t(
          "calendar:this_assignment_does_not_require_submission"
        ),
        backgroundColor: "var(--decorative-background-subtle-neutral)",
      };
    }
    case "TURNED_IN_LATE": {
      const dayCount = getDateTimeDifference({
        date1: submittedAt,
        date2: getDateMomentObject(),
        differenceBy: DATE_TIME_CONSTANTS.DAY,
      });

      const dateLabel = formatDate({
        date: submittedAt,
        format: DATE_TIME_CONSTANTS.DD_MMM,
      });
      const label =
        dayCount === 0
          ? t("calendar:today_lowercase")
          : dayCount === -1
          ? t("calendar:yesterday_lowercase")
          : `${t("common:lower_case_on")} ${dateLabel}`;
      return {
        icon: <TickCircleOutlined {...commonIconProps} variant={"warning"} />,
        studentSubmissionStatusLabel: t(
          "calendar:you_submitted_this_assignment",
          {
            label: label,
            time: formatDate({
              date: submittedAt,
              format: DATE_TIME_CONSTANTS.hh_mm_A,
            }),
          }
        ),
        parentSubmissionStatusLabel: t("calendar:assignment_was_submitted", {
          label: label,
          time: formatDate({
            date: submittedAt,
            format: DATE_TIME_CONSTANTS.hh_mm_A,
          }),
        }),
        backgroundColor: "var(--decorative-background-subtle-yellow)",
      };
    }
    case "TURNED_IN_ON_TIME": {
      const dateLabel = formatDate({
        date: submittedAt,
        format: DATE_TIME_CONSTANTS.DD_MM_YYYY,
      });

      const dayCount = getDateTimeDifference({
        date1: submittedAt,
        date2: getDateMomentObject(),
        differenceBy: DATE_TIME_CONSTANTS.DAY,
      });

      const label =
        dayCount === 0
          ? t("calendar:today_lowercase")
          : dayCount === -1
          ? t("calendar:yesterday_lowercase")
          : `${t("common:lower_case_on")} ${dateLabel}`;
      return {
        icon: <TickCircleOutlined {...commonIconProps} variant={"success"} />,
        studentSubmissionStatusLabel: t(
          "calendar:you_submitted_this_assignment",
          {
            label: label,
            time: formatDate({
              date: submittedAt,
              format: DATE_TIME_CONSTANTS.hh_mm_A,
            }),
          }
        ),
        parentSubmissionStatusLabel: t("calendar:assignment_was_submitted", {
          label: label,
          time: formatDate({
            date: submittedAt,
            format: DATE_TIME_CONSTANTS.hh_mm_A,
          }),
        }),
        backgroundColor: "var(--decorative-background-subtle-teal)",
      };
    }
    case "REQUEST_RESUBMISSION": {
      return {
        icon: (
          <ReSubmissionRequestOutlined
            {...commonIconProps}
            variant={"critical"}
          />
        ),
        studentSubmissionStatusLabel: t(
          "calendar:requested_resubmission_of_this_assignment"
        ),
        parentSubmissionStatusLabel: t(
          "calendar:requested_resubmission_of_this_assignment"
        ),
        backgroundColor: "var(--decorative-background-subtle-orange)",
      };
    }
    case "NOT_SUBMITTED":
    case "RESUBMISSION_NOT_SUBMITTED": {
      return {
        icon: <LockOutlined {...commonIconProps} variant={"critical"} />,
        studentSubmissionStatusLabel: t(
          "calendar:submissions_are_closed_for_this_assignment"
        ),
        parentSubmissionStatusLabel: t(
          "calendar:submissions_are_closed_for_this_assignment_for_parent"
        ),
        backgroundColor: "var(--decorative-background-subtle-pink)",
      };
    }
    default:
      return {
        showSubmissionStatus: false,
      };
  }
};

export const getIsCalendarAssignmentAssignFlowRevampEnabled = () => {
  return ACLStore.can("FeatureFlag:CalendarAssignFlowRevamp");
};

export const getTimeRangeProps = ({
  openDateRange,
  dueDateRange,
  closeDateRange,
}) => {
  let startTime = null,
    endTime = null;

  if (openDateRange) {
    startTime = formatDate({
      date: openDateRange?.minTime,
      format: DATE_TIME_CONSTANTS.HH_mm,
    });
    endTime = formatDate({
      date: openDateRange?.maxTime,
      format: DATE_TIME_CONSTANTS.HH_mm,
    });
  } else if (dueDateRange) {
    startTime = formatDate({
      date: dueDateRange?.minTime,
      format: DATE_TIME_CONSTANTS.HH_mm,
    });
    endTime = formatDate({
      date: dueDateRange?.maxTime,
      format: DATE_TIME_CONSTANTS.HH_mm,
    });
  } else if (closeDateRange) {
    startTime = formatDate({
      date: closeDateRange?.minTime,
      format: DATE_TIME_CONSTANTS.HH_mm,
    });
    endTime = formatDate({
      date: closeDateRange?.maxTime,
      format: DATE_TIME_CONSTANTS.HH_mm,
    });
  } else {
    return {};
  }

  const finalProps = {
    ...(startTime && !_.isEqual(startTime, "Invalid date")
      ? { startTime }
      : {}),
    ...(endTime && !_.isEqual(startTime, "Invalid date") ? { endTime } : {}),
  };

  return finalProps;
};

export const getIsSubmissionOpen = ({
  calendarWiseAssignedUsers,
  isParent,
  childId,
  userId,
}) => {
  const currentStudentStatus = _.find(
    _.get(_.first(calendarWiseAssignedUsers), "assignedUsers", []),
    ({ id }) => id === (isParent ? childId : userId)
  );

  const { lockingState } = currentStudentStatus;

  return !_.isEqual(lockingState, "LOCKED");
};

/**
 * Validates that the assignment dates follow the correct sequence: opens on <= due date <= closes on
 * @param {Object} params - The parameters object
 * @param {string} params.publishedAt - The opens on date (ISO string)
 * @param {string} params.dueDate - The due date (ISO string)
 * @param {string} params.closeSubmissionDate - The closes on date (ISO string)
 * @returns {Object} - An object with isValid boolean and message if invalid
 */
export const validateAssignmentDateSequence = ({
  publishedAt,
  dueDate,
  closeSubmissionDate,
}) => {
  const isPublishedDateValid = isValidDateTimeString(publishedAt);
  const isDueDateValid = isValidDateTimeString(dueDate);
  const isCloseSubmissionDateValid = isValidDateTimeString(closeSubmissionDate);

  // Check due date is NOT before published date (invalid if due date < opens on)
  if (
    isDueDateValid &&
    isPublishedDateValid &&
    checkIsDateBefore({
      date1: dueDate,
      date2: publishedAt,
      key: "minutes",
    })
  ) {
    return {
      isValid: false,
      message: "calendar:due_date_cannot_be_before_open_date",
    };
  }

  // Check close date is NOT before due date (invalid if closes on < due date)
  if (
    isDueDateValid &&
    isCloseSubmissionDateValid &&
    checkIsDateBefore({
      date1: closeSubmissionDate,
      date2: dueDate,
      key: "minutes",
    })
  ) {
    return {
      isValid: false,
      message: "calendar:close_date_cannot_be_before_due_date",
    };
  }

  // Check close date is NOT before published date (invalid if closes on < opens on)
  if (
    isPublishedDateValid &&
    isCloseSubmissionDateValid &&
    checkIsDateBefore({
      date1: closeSubmissionDate,
      date2: publishedAt,
      key: "minutes",
    })
  ) {
    return {
      isValid: false,
      message: "calendar:close_date_cannot_be_before_open_date",
    };
  }

  return { isValid: true };
};

export const isNewQuickViewEditFFEnabled = () => {
  return ACLStore.can("FeatureFlag:CompactQuickView");
};

export const ASSIGNMENT_STATUS_MAPPING = {
  OPEN: "OPEN",
  CLOSED: "CLOSED",
  DRAFT: "DRAFT",
  TURNED_IN_ON_TIME: "TURNED_IN_ON_TIME",
  TURNED_IN_LATE: "TURNED_IN_LATE",
  SUBMISSION_PENDING: "SUBMISSION_PENDING",
  REQUEST_RESUBMISSION: "REQUEST_RESUBMISSION",
  REQUEST_RESUBMISSION_OVERDUE: "REQUEST_RESUBMISSION_OVERDUE",
  SUBMISSION_NOT_REQUIRED: "SUBMISSION_NOT_REQUIRED",
  OVERDUE: "OVERDUE",
  NOT_SUBMITTED: "NOT_SUBMITTED",
  RESUBMISSION_NOT_SUBMITTED: "RESUBMISSION_NOT_SUBMITTED",
  SCHEDULED: "SCHEDULED",
};

export const getAssignmentComponentByStatus = ({ status, t = _.noop }) => {
  const wrapperStyle = { display: "flex", alignItems: "center", gap: "4px" };
  switch (status) {
    case ASSIGNMENT_STATUS_MAPPING.OPEN:
      return (
        <div style={wrapperStyle}>
          <LockOpenOutlined dsVersion="2.0" size="xxx-small" />
          <span className="text-body-s">{t("common:open")}</span>
        </div>
      );

    case ASSIGNMENT_STATUS_MAPPING.CLOSED:
      return (
        <div style={wrapperStyle}>
          <LockOutlined dsVersion="2.0" size="xxx-small" />
          <span className="text-body-s">{t("common:closed")}</span>
        </div>
      );

    default:
      return null;
  }
};

/**
 * Determines if a user can join a meeting based on their role and meeting participants.
 *
 * Any user who can see the meeting should be able to join the meeting,
 * EXCEPT parents who are NOT part of the meeting (this is when parent are viewing child's calendar)
 *
 * For grade/course-level meetings, "PARENT" in sharedWith means all parents
 * are invited. For custom-participant meetings, we additionally verify that
 * the specific parent is present in the users list.
 *
 * @param {Object} params - The parameters object
 * @param {boolean} params.isParent - Whether the user is a parent
 * @param {Array<string>} [params.sharedWith] - User types invited, e.g. ["STAFF","STUDENT","PARENT"]
 * @param {Array<{__typename: string}>} [params.associatedEntities] - Grade/Course entities (empty for custom)
 * @param {Array<{id: string, __typename: string}>} [params.users] - Custom participant list
 * @returns {boolean} True if the user can join the meeting, false otherwise
 */
export const getCanUserJoinMeeting = ({
  isParent,
  sharedWith,
  associatedEntities,
  users,
}) => {
  if (!isParent) {
    return true;
  }

  if (!_.includes(sharedWith, "PARENT")) {
    return false;
  }

  const typeName = _.get(associatedEntities, "0.__typename", "");
  const isGradeOrCourseLevel = typeName === "Grade" || typeName === "Course";

  if (isGradeOrCourseLevel) {
    return true;
  }

  // Custom participants — verify this specific parent is in the users list
  const parentUsers = _.filter(users, { __typename: "FamilyMember" });
  return _.some(parentUsers, { id: getUserId() });
};
